import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown, HardHat, UserCheck, ClipboardCheck, Globe } from "lucide-react";
import { IMAGES, scrollToId } from "../../lib/site";

const LINES = ["Votre chantier.", "Vos besoins.", "Nos solutions."];

const REASSURANCE = [
    { icon: HardHat, label: "Matériel professionnel", testid: "hero-reassurance-materiel" },
    { icon: UserCheck, label: "Interlocuteur dédié", testid: "hero-reassurance-interlocuteur" },
    { icon: ClipboardCheck, label: "Étude personnalisée de votre besoin", testid: "hero-reassurance-etude" },
    { icon: Globe, label: "Couverture nationale", testid: "hero-reassurance-couverture" },
];

export const Hero = () => {
    const { scrollY } = useScroll();
    const y = useTransform(scrollY, [0, 900], [0, 220]);
    const opacity = useTransform(scrollY, [0, 600], [1, 0.35]);

    return (
        <section
            id="accueil"
            data-testid="hero-section"
            className="grain relative flex min-h-[100svh] items-end overflow-hidden bg-ink"
        >
            <motion.div style={{ y }} className="absolute inset-0 -bottom-56">
                <img
                    src={IMAGES.hero}
                    alt="Chantier de construction au crépuscule"
                    className="h-full w-full object-cover"
                    fetchpriority="high"
                />
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/30" />
            <div
                className="pointer-events-none absolute -left-40 top-1/3 h-[480px] w-[480px] rounded-full opacity-25"
                style={{ background: "radial-gradient(circle, #FF5722 0%, transparent 65%)" }}
            />
            <motion.div
                style={{ opacity }}
                className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-28 pt-40 lg:px-8 lg:pb-36"
            >
                <motion.p
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="mb-6 font-mono text-xs uppercase tracking-[0.3em] text-signal sm:text-sm"
                    data-testid="hero-eyebrow"
                >
                    Location de matériel &amp; solutions BTP · Partout en France
                </motion.p>
                <h1
                    data-testid="hero-title"
                    className="font-display text-[clamp(2.6rem,8.5vw,6.5rem)] font-black leading-[0.98] tracking-tight text-white"
                >
                    {LINES.map((line, i) => (
                        <span key={line} className="block overflow-hidden pb-1">
                            <motion.span
                                className={`block ${i === 2 ? "text-signal" : ""}`}
                                initial={{ y: "110%" }}
                                animate={{ y: 0 }}
                                transition={{ duration: 0.9, delay: 0.2 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                            >
                                {line}
                            </motion.span>
                        </span>
                    ))}
                </h1>
                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.75 }}
                    className="mt-7 max-w-2xl text-base leading-relaxed text-zinc-300 sm:text-lg"
                    data-testid="hero-subtitle"
                >
                    Location de matériel de chantier et solutions BTP pour les professionnels
                    partout en France. NEBOORZY vous accompagne directement sur le terrain pour
                    étudier votre besoin et vous proposer une solution adaptée.
                </motion.p>
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.9 }}
                    className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center"
                >
                    <button
                        type="button"
                        data-testid="hero-cta-primary"
                        onClick={() => scrollToId("besoin")}
                        className="group inline-flex items-center justify-center gap-2 rounded-full bg-signal px-7 py-4 text-base font-bold text-ink transition-colors hover:bg-signal-dark"
                    >
                        Demander le passage d'un commercial
                        <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </button>
                    <button
                        type="button"
                        data-testid="hero-cta-secondary"
                        onClick={() => scrollToId("solutions")}
                        className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 px-7 py-4 text-base font-semibold text-white transition-colors hover:border-white/60 hover:bg-white/5"
                    >
                        Découvrir nos solutions
                    </button>
                </motion.div>
                <motion.ul
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 1.1 }}
                    className="mt-12 flex flex-col gap-3 border-t border-white/10 pt-6 sm:flex-row sm:gap-10"
                    data-testid="hero-reassurance-list"
                >
                    {REASSURANCE.map(({ icon: Icon, label, testid }) => (
                        <li key={label} data-testid={testid} className="flex items-center gap-2.5 text-sm text-zinc-300">
                            <Icon className="h-4 w-4 text-signal" />
                            {label}
                        </li>
                    ))}
                </motion.ul>
            </motion.div>
            <motion.button
                type="button"
                data-testid="hero-scroll-indicator"
                onClick={() => scrollToId("presentation")}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5 }}
                className="absolute bottom-6 right-6 z-10 hidden text-zinc-400 transition-colors hover:text-signal lg:block"
                aria-label="Faire défiler"
            >
                <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }} className="block">
                    <ArrowDown className="h-6 w-6" />
                </motion.span>
            </motion.button>
        </section>
    );
};
